/**
 * L'agenda, adossé à Supabase.
 *
 * Toute la logique — la journée locale, le prochain rendez-vous, ce qui est
 * passé — vit dans `core`. Cet adaptateur lit, écrit, et joint deux noms : le
 * client et le chantier, pour que le tableau de bord dise « métré chez Durand »
 * sans une requête par ligne.
 */
import { DomainError } from "@dker/core";
import type {
  Appointment,
  AppointmentRepository,
  ExecutionContext,
  SaveAppointmentInput,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

const one = (raw: unknown): Row | null =>
  raw == null ? null : ((Array.isArray(raw) ? raw[0] : raw) as Row | null) ?? null;

function clientName(raw: unknown): string | null {
  const c = one(raw);
  if (!c) return null;
  return c.company_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || null;
}

function toAppointment(row: Row): Appointment {
  return {
    id: row.id,
    startsAt: row.starts_at,
    durationMinutes: row.duration_minutes == null ? null : Number(row.duration_minutes),
    kind: row.kind,
    title: row.title,
    clientId: row.client_id ?? null,
    clientName: clientName(row.clients),
    projectId: row.project_id ?? null,
    projectName: one(row.projects)?.name ?? null,
    location: row.location ?? null,
    notes: row.notes ?? null,
  };
}

const COLONNES = `
  id, starts_at, duration_minutes, kind, title, client_id, project_id, location, notes,
  clients(first_name, last_name, company_name), projects(name)
`;

export function createSupabaseAppointmentRepository(
  clientFor: DbClientFactory,
): AppointmentRepository {
  return {
    async list(ctx: ExecutionContext, from: string, to: string) {
      const { data, error } = await clientFor(ctx)
        .from("appointments")
        .select(COLONNES)
        .gte("starts_at", from)
        .lt("starts_at", to)
        .order("starts_at", { ascending: true });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toAppointment);
    },

    async save(ctx: ExecutionContext, command: Omit<SaveAppointmentInput, "confirmationId">) {
      const ligne = {
        starts_at: command.startsAt,
        duration_minutes: command.durationMinutes,
        kind: command.kind,
        title: command.title,
        client_id: command.clientId,
        project_id: command.projectId,
        location: command.location,
        notes: command.notes,
      };
      const q = clientFor(ctx).from("appointments");
      const { data, error } = command.id
        ? await q.update(ligne).eq("id", command.id).select(COLONNES).single()
        : await q.insert(ligne).select(COLONNES).single();
      if (error) fail(error);
      return toAppointment(data as Row);
    },

    async remove(ctx: ExecutionContext, id: string) {
      const { error } = await clientFor(ctx).from("appointments").delete().eq("id", id);
      if (error) fail(error);
    },
  };
}
